import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronBack, IoSend, IoAdd, IoDocumentOutline, IoDownloadOutline, IoExitOutline } from 'react-icons/io5';
import { db, storage } from '../../firebase/config';
import {
  doc, collection, query, orderBy, onSnapshot,
  addDoc, updateDoc, deleteDoc, serverTimestamp, getDoc, getDocs, arrayRemove,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { format, isToday, isYesterday } from 'date-fns';
import { compressDetailImage } from '../../utils/imageUtils';

const ROLE_LABELS = { teacher: '선생님', student: '학생', parent: '학부모', assistant: '조교' };

const formatMsgTime = (timestamp) => {
  if (!timestamp) return '';
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return format(d, 'a h:mm').replace('AM', '오전').replace('PM', '오후');
};

const formatDateDivider = (timestamp) => {
  if (!timestamp) return '';
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isToday(d)) return '오늘';
  if (isYesterday(d)) return '어제';
  return format(d, 'yyyy년 M월 d일');
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

const ChatDetailPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const myUid = userData?.uid;

  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  // 채팅방 정보 로드
  useEffect(() => {
    if (!roomId) return;
    const unsub = onSnapshot(doc(db, 'chatRooms', roomId), (snap) => {
      if (snap.exists()) setRoom({ id: snap.id, ...snap.data() });
    });
    return () => unsub();
  }, [roomId]);

  // 입장 시 안 읽은 수 초기화
  const clearUnread = useCallback(async () => {
    if (!roomId || !myUid) return;
    const roomRef = doc(db, 'chatRooms', roomId);
    const snap = await getDoc(roomRef);
    if (!snap.exists()) return;
    if ((snap.data().unreadCount?.[myUid] || 0) > 0) {
      await updateDoc(roomRef, { [`unreadCount.${myUid}`]: 0 });
    }
  }, [roomId, myUid]);

  useEffect(() => { clearUnread(); }, [clearUnread]);

  // 메시지 실시간 수신
  useEffect(() => {
    if (!roomId) return;
    const q = query(
      collection(db, 'chatRooms', roomId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      clearUnread();
    });
    return () => unsub();
  }, [roomId, clearUnread]);

  // 메시지 추가 시 스크롤
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 메시지 전송
  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending || !myUid) return;
    setSending(true);
    setText('');
    try {
      await addDoc(collection(db, 'chatRooms', roomId, 'messages'), {
        text: trimmed,
        senderId: myUid,
        senderName: userData?.name || '',
        createdAt: serverTimestamp(),
      });
      const roomRef = doc(db, 'chatRooms', roomId);
      const roomSnap = await getDoc(roomRef);
      if (roomSnap.exists()) {
        const roomData = roomSnap.data();
        const updates = { lastMessage: trimmed, lastMessageAt: serverTimestamp() };
        (roomData.participants || []).forEach(uid => {
          if (uid !== myUid) {
            updates[`unreadCount.${uid}`] = (roomData.unreadCount?.[uid] || 0) + 1;
          }
        });
        await updateDoc(roomRef, updates);
      }
    } catch (err) {
      console.error('메시지 전송 실패:', err);
    } finally {
      setSending(false);
    }
  };

  // 파일 전송
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploading || !myUid) return;
    e.target.value = '';
    setUploading(true);
    try {
      const isImage = file.type.startsWith('image/');
      let uploadFile = file;
      if (isImage) {
        uploadFile = await compressDetailImage(file, 800, 0.7);
      }
      const storageRef = ref(storage, `chat/${roomId}/${Date.now()}_${uploadFile.name}`);
      await uploadBytes(storageRef, uploadFile);
      const url = await getDownloadURL(storageRef);

      await addDoc(collection(db, 'chatRooms', roomId, 'messages'), {
        text: '',
        senderId: myUid,
        senderName: userData?.name || '',
        createdAt: serverTimestamp(),
        type: isImage ? 'image' : 'file',
        fileUrl: url,
        fileName: file.name,
        fileSize: file.size,
      });

      const roomRef = doc(db, 'chatRooms', roomId);
      const roomSnap = await getDoc(roomRef);
      if (roomSnap.exists()) {
        const roomData = roomSnap.data();
        const updates = {
          lastMessage: isImage ? '사진을 보냈습니다' : `파일: ${file.name}`,
          lastMessageAt: serverTimestamp(),
        };
        (roomData.participants || []).forEach(uid => {
          if (uid !== myUid) {
            updates[`unreadCount.${uid}`] = (roomData.unreadCount?.[uid] || 0) + 1;
          }
        });
        await updateDoc(roomRef, updates);
      }
    } catch (err) {
      console.error('파일 전송 실패:', err);
    } finally {
      setUploading(false);
    }
  };

  // 채팅방 나가기
  const handleLeaveRoom = async () => {
    if (!window.confirm('채팅방을 나가시겠습니까?\n대화 내용은 삭제됩니다.')) return;
    try {
      const roomRef = doc(db, 'chatRooms', roomId);
      const roomSnap = await getDoc(roomRef);
      if (!roomSnap.exists()) { navigate('/chat'); return; }
      const roomData = roomSnap.data();
      const remaining = (roomData.participants || []).filter(uid => uid !== myUid);
      if (remaining.length === 0) {
        const msgSnap = await getDocs(collection(db, 'chatRooms', roomId, 'messages'));
        for (const m of msgSnap.docs) await deleteDoc(m.ref);
        await deleteDoc(roomRef);
      } else {
        const nameUpdates = { ...roomData.participantNames };
        delete nameUpdates[myUid];
        const roleUpdates = { ...roomData.participantRoles };
        delete roleUpdates[myUid];
        await updateDoc(roomRef, {
          participants: arrayRemove(myUid),
          participantNames: nameUpdates,
          participantRoles: roleUpdates,
          [`unreadCount.${myUid}`]: 0,
        });
      }
      navigate('/chat');
    } catch (err) {
      console.error('채팅방 나가기 실패:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // 상대방 이름 + 역할
  const otherUid = room ? (room.participants || []).find(uid => uid !== myUid) : null;
  const roomName = (() => {
    if (!room) return '';
    if (room.roomName) return room.roomName;
    const names = room.participantNames || {};
    return names[otherUid] || '채팅';
  })();
  const otherRole = room ? (room.participantRoles || {})[otherUid] || '' : '';

  // 날짜 구분선
  const getDateKey = (msg) => {
    if (!msg.createdAt) return '';
    const d = msg.createdAt.toDate ? msg.createdAt.toDate() : new Date(msg.createdAt);
    return format(d, 'yyyy-MM-dd');
  };

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate('/chat')}>
          <IoChevronBack size={24} />
        </BackBtn>
        <HeaderCenter>
          <HeaderTitle>{roomName}</HeaderTitle>
          {otherRole && <HeaderRole>{ROLE_LABELS[otherRole] || otherRole}</HeaderRole>}
        </HeaderCenter>
        <LeaveBtn onClick={handleLeaveRoom}>
          <IoExitOutline size={20} />
        </LeaveBtn>
      </Header>

      <MessageList>
        {messages.map((msg, i) => {
          const isMine = msg.senderId === myUid;
          const dateKey = getDateKey(msg);
          const prevDateKey = i > 0 ? getDateKey(messages[i - 1]) : '';
          const showDate = dateKey && dateKey !== prevDateKey;

          return (
            <React.Fragment key={msg.id}>
              {showDate && (
                <DateDivider>
                  <DateLabel>{formatDateDivider(msg.createdAt)}</DateLabel>
                </DateDivider>
              )}
              <MsgRow $isMine={isMine}>
                {!isMine && (
                  <SenderAvatar>{(msg.senderName || '?').charAt(0)}</SenderAvatar>
                )}
                <MsgContent $isMine={isMine}>
                  {!isMine && <SenderName>{msg.senderName}</SenderName>}
                  <BubbleRow $isMine={isMine}>
                    {isMine && <MsgTime>{formatMsgTime(msg.createdAt)}</MsgTime>}
                    {msg.type === 'image' ? (
                      <ImageBubble $isMine={isMine} onClick={() => window.open(msg.fileUrl, '_blank')}>
                        <ChatImage src={msg.fileUrl} alt="" />
                      </ImageBubble>
                    ) : msg.type === 'file' ? (
                      <FileBubble $isMine={isMine} onClick={() => window.open(msg.fileUrl, '_blank')}>
                        <FileIcon $isMine={isMine}><IoDocumentOutline size={20} /></FileIcon>
                        <FileInfo>
                          <FileName $isMine={isMine}>{msg.fileName}</FileName>
                          <FileSize $isMine={isMine}>{formatFileSize(msg.fileSize)}</FileSize>
                        </FileInfo>
                        <DownloadIcon $isMine={isMine}><IoDownloadOutline size={18} /></DownloadIcon>
                      </FileBubble>
                    ) : (
                      <Bubble $isMine={isMine}>{msg.text}</Bubble>
                    )}
                    {!isMine && <MsgTime>{formatMsgTime(msg.createdAt)}</MsgTime>}
                  </BubbleRow>
                </MsgContent>
              </MsgRow>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </MessageList>

      <InputArea>
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.hwp,.zip,.txt"
          onChange={handleFileSelect}
        />
        <InputWrap>
          <AttachBtn onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            <IoAdd size={22} />
          </AttachBtn>
          <TextInput
            placeholder={uploading ? '파일 전송 중...' : '메시지를 입력하세요'}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={uploading}
          />
          <SendBtn onClick={handleSend} disabled={!text.trim() || sending || uploading}>
            <IoSend size={18} />
          </SendBtn>
        </InputWrap>
      </InputArea>
    </Container>
  );
};

export default ChatDetailPage;

/* ─── Styled Components ──────────────────── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  background: #F7F8FA;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 16px;
  background: #fff;
  border-bottom: 1px solid #E5E7EB;
  flex-shrink: 0;
`;

const BackBtn = styled.button`
  color: #1A1A2E;
  display: flex;
  align-items: center;
  padding: 4px;
`;

const HeaderCenter = styled.div`
  flex: 1;
  text-align: center;
`;

const HeaderTitle = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: #1A1A2E;
`;

const HeaderRole = styled.p`
  font-size: 11px;
  color: #9CA3AF;
  margin-top: 1px;
`;

const LeaveBtn = styled.button`
  color: #9CA3AF;
  display: flex;
  align-items: center;
  padding: 4px;
  &:active { color: #EF4444; }
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  -webkit-overflow-scrolling: touch;
`;

const DateDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0 12px;
`;

const DateLabel = styled.span`
  font-size: 11px;
  color: #9CA3AF;
  background: #E5E7EB;
  padding: 4px 12px;
  border-radius: 10px;
`;

const MsgRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 10px;
  justify-content: ${({ $isMine }) => $isMine ? 'flex-end' : 'flex-start'};
`;

const SenderAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #E5E7EB;
  color: #6B7280;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 2px;
`;

const MsgContent = styled.div`
  max-width: 70%;
  display: flex;
  flex-direction: column;
  align-items: ${({ $isMine }) => $isMine ? 'flex-end' : 'flex-start'};
`;

const SenderName = styled.span`
  font-size: 12px;
  color: #9CA3AF;
  margin-bottom: 4px;
  padding-left: 2px;
`;

const BubbleRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
`;

const Bubble = styled.div`
  padding: 10px 14px;
  border-radius: ${({ $isMine }) => $isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px'};
  background: ${({ $isMine }) => $isMine ? '#4A7CFE' : '#FFFFFF'};
  color: ${({ $isMine }) => $isMine ? '#FFFFFF' : '#1A1A2E'};
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;
  box-shadow: ${({ $isMine }) => $isMine ? 'none' : '0 1px 3px rgba(0,0,0,0.04)'};
`;

const MsgTime = styled.span`
  font-size: 10px;
  color: #9CA3AF;
  flex-shrink: 0;
  white-space: nowrap;
`;

const ImageBubble = styled.div`
  border-radius: ${({ $isMine }) => $isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px'};
  overflow: hidden;
  cursor: pointer;
  max-width: 200px;
`;

const ChatImage = styled.img`
  display: block;
  width: 100%;
  max-height: 250px;
  object-fit: cover;
`;

const FileBubble = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: ${({ $isMine }) => $isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px'};
  background: ${({ $isMine }) => $isMine ? '#4A7CFE' : '#FFFFFF'};
  box-shadow: ${({ $isMine }) => $isMine ? 'none' : '0 1px 3px rgba(0,0,0,0.04)'};
  cursor: pointer;
  min-width: 180px;
`;

const FileIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $isMine }) => $isMine ? 'rgba(255,255,255,0.15)' : '#EEF2FF'};
  color: ${({ $isMine }) => $isMine ? '#fff' : '#4A7CFE'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $isMine }) => $isMine ? '#fff' : '#1A1A2E'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.p`
  font-size: 11px;
  color: ${({ $isMine }) => $isMine ? 'rgba(255,255,255,0.6)' : '#9CA3AF'};
  margin-top: 2px;
`;

const DownloadIcon = styled.div`
  color: ${({ $isMine }) => $isMine ? 'rgba(255,255,255,0.6)' : '#9CA3AF'};
  flex-shrink: 0;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const InputArea = styled.div`
  padding: 8px 12px;
  padding-bottom: max(8px, env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid #E5E7EB;
  flex-shrink: 0;
`;

const InputWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: #F3F4F6;
  border-radius: 22px;
  padding: 6px 6px 6px 16px;
`;

const AttachBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  color: ${({ disabled }) => disabled ? '#D1D5DB' : '#4A7CFE'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:active:not(:disabled) { background: rgba(74,124,254,0.1); }
`;

const TextInput = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #1A1A2E;
  background: transparent;
  resize: none;
  max-height: 100px;
  line-height: 1.5;
  padding: 6px 0;
  &::placeholder { color: #D1D5DB; }
`;

const SendBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ disabled }) => disabled ? '#D1D5DB' : '#4A7CFE'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
  &:active:not(:disabled) { opacity: 0.8; }
`;
